import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Empty, LifecycleChip, Loading, PageHeader, Panel, SeverityChip, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRY_NAME, LIFECYCLE_ORDER, shortDate } from "@/lib/format";

const BLANK = {
  title: "", summary: "", jurisdiction_country: "IN", regulator: "", citation: "",
  severity: "warn", obligation: "", remediation: "", effective_from: new Date().toISOString().slice(0, 10),
  applicability: { country: "IN", transaction_type: "donation" }, conditions: [],
};

export default function RuleWorkbench() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [rules, setRules] = useState(null);
  const [stats, setStats] = useState(null);
  const [state, setState] = useState("ALL");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [r, s] = await Promise.all([api.get("/regulatory/rules"), api.get("/regulatory/rules/stats")]);
    setRules(r.data);
    setStats(s.data);
  }, []);

  useEffect(() => {
    load().catch(() => setRules([]));
  }, [load]);

  const filtered = useMemo(() => {
    if (!rules) return [];
    return rules.filter((r) =>
      (state === "ALL" || r.lifecycle_state === state) &&
      (!q || r.title.toLowerCase().includes(q.toLowerCase()) || r.rule_key.toLowerCase().includes(q.toLowerCase())));
  }, [rules, state, q]);

  const generate = async () => {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/draft", { prompt: aiPrompt, mode: "rule_draft" });
      if (data.draft) {
        setForm((f) => ({ ...f, ...data.draft, effective_from: data.draft.effective_from || f.effective_from }));
        toast.success("Draft generated by GPT-5.5 — review before saving");
      } else {
        toast.error("The model did not return valid rule JSON. Try rephrasing.");
      }
    } catch (e) {
      toast.error(apiError(e));
    }
    setAiBusy(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/regulatory/rules", form);
      toast.success(`${data.rule_key} v${data.version} created as DRAFT`);
      setOpen(false);
      setForm(BLANK);
      await load();
      navigate(`/regulatory/rules/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
    setSaving(false);
  };

  if (!rules) return <Loading label="Loading rule versions" />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-013 · Rule Workbench"
        title="Versioned, evidence-backed regulatory rules"
        description="Every substantive change creates a new immutable version. Only fully gated PUBLISHED versions can hard-block a transaction."
        actions={can("rule", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-rule-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> New rule version
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-border bg-card">
              <DialogHeader>
                <DialogTitle>Draft a regulatory rule</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Rules enter the lifecycle as DRAFT and must pass all six automated tests, analyst review
                  and legal review before approval and publication.
                </DialogDescription>
              </DialogHeader>

              <div className="rounded border border-violet-500/30 bg-violet-500/10 p-3">
                <div className="kicker mb-2 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-violet-600 dark:text-violet-400" /> AI drafting assistant (GPT-5.5)
                </div>
                <Textarea
                  rows={2}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  data-testid="ai-rule-prompt"
                  placeholder="e.g. Block any Indian donation above INR 20,000 paid in cash where the donor has no PAN"
                  className="bg-muted/40 text-xs"
                />
                <Button size="sm" onClick={generate} disabled={aiBusy} data-testid="ai-generate-button"
                        className="mt-2 h-7 rounded-full bg-violet-500/90 px-3 text-[11px] text-white hover:bg-violet-400">
                  {aiBusy ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1.5 h-3 w-3" />}
                  Generate draft
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Title" className="sm:col-span-2">
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                         data-testid="rule-title-input" className="bg-muted/40" />
                </Field>
                <Field label="Summary" className="sm:col-span-2">
                  <Textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
                            data-testid="rule-summary-input" className="bg-muted/40 text-xs" />
                </Field>
                <Field label="Jurisdiction (ISO-2 / GL)">
                  <Input value={form.jurisdiction_country}
                         onChange={(e) => setForm({ ...form, jurisdiction_country: e.target.value.toUpperCase() })}
                         data-testid="rule-country-input" className="bg-muted/40 font-mono" />
                </Field>
                <Field label="Blocking severity">
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger data-testid="rule-severity-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["block", "require_approval", "warn", "inform"].map((s) => (
                        <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Regulator">
                  <Input value={form.regulator} onChange={(e) => setForm({ ...form, regulator: e.target.value })}
                         data-testid="rule-regulator-input" className="bg-muted/40" />
                </Field>
                <Field label="Citation">
                  <Input value={form.citation} onChange={(e) => setForm({ ...form, citation: e.target.value })}
                         data-testid="rule-citation-input" className="bg-muted/40 font-mono text-xs" />
                </Field>
                <Field label="Effective from">
                  <Input type="date" value={form.effective_from}
                         onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                         data-testid="rule-effective-from-input" className="bg-muted/40 font-mono" />
                </Field>
                <Field label="Obligation">
                  <Input value={form.obligation} onChange={(e) => setForm({ ...form, obligation: e.target.value })}
                         data-testid="rule-obligation-input" className="bg-muted/40" />
                </Field>
                <Field label="Applicability (JSON — 12-layer cascade)" className="sm:col-span-2">
                  <Textarea rows={3} value={JSON.stringify(form.applicability, null, 0)}
                            onChange={(e) => {
                              try { setForm({ ...form, applicability: JSON.parse(e.target.value) }); } catch { /* keep */ }
                            }}
                            data-testid="rule-applicability-input" className="bg-muted/40 font-mono text-[11px]" />
                </Field>
                <Field label="Conditions (JSON array of {field, operator, value})" className="sm:col-span-2">
                  <Textarea rows={3} value={JSON.stringify(form.conditions, null, 0)}
                            onChange={(e) => {
                              try { setForm({ ...form, conditions: JSON.parse(e.target.value) }); } catch { /* keep */ }
                            }}
                            data-testid="rule-conditions-input" className="bg-muted/40 font-mono text-[11px]" />
                </Field>
              </div>

              <Button onClick={save} disabled={saving || !form.title} data-testid="rule-save-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create DRAFT version
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      {stats && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
          {LIFECYCLE_ORDER.map((s) => (
            <button key={s} onClick={() => setState(state === s ? "ALL" : s)}
                    data-testid={`lifecycle-filter-${s}`}
                    className={`panel px-3 py-2.5 text-left transition-colors duration-150 hover:bg-muted ${state === s ? "ring-1 ring-sky-500" : ""}`}>
              <div className="font-mono text-lg font-semibold text-foreground">{stats.by_state[s] || 0}</div>
              <div className="mt-0.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                {s.replace(/_/g, " ")}
              </div>
            </button>
          ))}
        </div>
      )}

      <Panel
        testId="panel-rules"
        kicker={`${filtered.length} version${filtered.length === 1 ? "" : "s"}`}
        title={state === "ALL" ? "All rule versions" : `${state.replace(/_/g, " ")} versions`}
        actions={
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rule key or title"
                   data-testid="rule-search-input" className="h-8 w-52 bg-muted/40 pl-8 text-xs" />
          </div>
        }
      >
        <Table testId="rules-table"
               head={<><Th>Rule key</Th><Th>Title</Th><Th>Market</Th><Th>Severity</Th><Th>State</Th><Th>Evidence</Th><Th>Tests</Th><Th>Effective</Th></>}>
          {filtered.length === 0 && <Empty colSpan={8} label="No rule versions match" />}
          {filtered.map((r) => (
            <tr key={r.id} className="row-hover cursor-pointer" data-testid={`rule-row-${r.rule_key}`}
                onClick={() => navigate(`/regulatory/rules/${r.id}`)}>
              <Td>
                <span className="font-mono text-[11px] text-primary">{r.rule_key}</span>
                <span className="ml-1.5 font-mono text-[10px] text-muted-foreground/70">v{r.version}</span>
              </Td>
              <Td className="max-w-[320px]">
                <div className="truncate text-foreground">{r.title}</div>
                <div className="truncate text-[11px] text-muted-foreground">{r.citation}</div>
              </Td>
              <Td>
                <span className="font-mono text-xs text-foreground/80">{r.jurisdiction_country}</span>
                <span className="ml-1.5 text-[11px] text-muted-foreground">{COUNTRY_NAME[r.jurisdiction_country] || ""}</span>
              </Td>
              <Td><SeverityChip severity={r.severity} testId={`rule-severity-${r.rule_key}`} /></Td>
              <Td><LifecycleChip state={r.lifecycle_state} testId={`rule-state-${r.rule_key}`} /></Td>
              <Td>
                <Chip className={r.evidence_status === "independently_evidenced"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "border-border bg-muted text-muted-foreground"}>
                  {r.evidence_status === "independently_evidenced" ? "Evidenced" : "Candidate"}
                </Chip>
              </Td>
              <Td className="font-mono text-[11px]">
                {r.test_summary?.total
                  ? <span className={r.test_summary.passed ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}>
                      {r.test_summary.passed_count}/{r.test_summary.total}
                    </span>
                  : <span className="text-muted-foreground/70">not run</span>}
              </Td>
              <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(r.effective_from)}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
